import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Plus, Users, Phone, Shield, Building, Edit, Trash2, Key, Loader2, Search, UserCheck, UserX, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PhoneInput } from '../components/ui/phone-input';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPinModal, setShowResetPinModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [formData, setFormData] = useState({
    phone: '',
    first_name: '',
    last_name: '',
    role: 'office_admin',
    office_id: '',
    pin: ''
  });
  const [newPin, setNewPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pinResetResult, setPinResetResult] = useState(null);

  const { getAuthHeaders, user: currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (roleFilter && roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await fetch(`${API_URL}/api/users/?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/offices/`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOffices(data);
      }
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchOffices()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [roleFilter, searchQuery]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const userData = {
        phone: formData.phone,
        first_name: formData.first_name,
        last_name: formData.last_name,
        roles: [{
          role: formData.role,
          office_id: formData.role !== 'super_admin' ? formData.office_id : null
        }]
      };

      if (formData.pin) {
        userData.pin = formData.pin;
      }

      const response = await fetch(`${API_URL}/api/users/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        await fetchUsers();
        setShowAddModal(false);
        resetForm();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        roles: [{
          role: formData.role,
          office_id: formData.role !== 'super_admin' ? formData.office_id : null
        }]
      };

      const response = await fetch(`${API_URL}/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        await fetchUsers();
        setShowEditModal(false);
        resetForm();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPin = async () => {
    setError('');
    setSubmitting(true);
    setPinResetResult(null);

    try {
      const response = await fetch(`${API_URL}/api/users/reset-pin`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: selectedUser.id,
          new_pin: newPin || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPinResetResult(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to reset PIN');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: true })
      });

      if (response.ok) {
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error reactivating user:', error);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    const primaryRole = user.roles?.[0];
    setFormData({
      phone: user.phone,
      first_name: user.first_name,
      last_name: user.last_name,
      role: primaryRole?.role || 'office_admin',
      office_id: primaryRole?.office_id || '',
      pin: ''
    });
    setShowEditModal(true);
  };

  const openResetPinModal = (user) => {
    setSelectedUser(user);
    setNewPin('');
    setPinResetResult(null);
    setShowResetPinModal(true);
  };

  const resetForm = () => {
    setFormData({
      phone: '',
      first_name: '',
      last_name: '',
      role: 'office_admin',
      office_id: '',
      pin: ''
    });
    setSelectedUser(null);
    setError('');
    setNewPin('');
    setPinResetResult(null);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return <Badge className="bg-purple-100 text-purple-800"><Shield className="h-3 w-3 mr-1" />Super Admin</Badge>;
      case 'office_admin':
        return <Badge className="bg-blue-100 text-blue-800"><Building className="h-3 w-3 mr-1" />Office Admin</Badge>;
      case 'employee':
        return <Badge className="bg-green-100 text-green-800"><UserCheck className="h-3 w-3 mr-1" />Employee</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const getOfficeName = (officeId) => {
    const office = offices.find(o => o.id === officeId);
    return office ? office.name : 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="users-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manage-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manage Users</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage system users, admins, and their access permissions
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="flex items-center gap-2"
          data-testid="add-user-button"
        >
          <Plus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Super Admins</p>
                <p className="text-2xl font-bold">
                  {users.filter(u => u.roles?.some(r => r.role === 'super_admin')).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Office Admins</p>
                <p className="text-2xl font-bold">
                  {users.filter(u => u.roles?.some(r => r.role === 'office_admin')).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <UserX className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold">
                  {users.filter(u => !u.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-users-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="office_admin">Office Admin</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* User List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => (
          <Card
            key={user.id}
            className={`hover:shadow-md transition-shadow ${!user.is_active ? 'opacity-60' : ''}`}
            data-testid={`user-card-${user.id}`}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${user.is_active ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <span className={`font-semibold text-lg ${user.is_active ? 'text-blue-600' : 'text-gray-400'}`}>
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{user.full_name}</h3>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Phone className="h-3 w-3" />
                        {user.phone}
                      </div>
                    </div>
                  </div>
                  {!user.is_active && (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </div>

                {/* Role & Office */}
                <div className="space-y-2">
                  {user.roles?.map((role, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      {getRoleBadge(role.role)}
                      {role.office_id && (
                        <span className="text-sm text-gray-500">{getOfficeName(role.office_id)}</span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Meta */}
                <div className="text-xs text-gray-500 space-y-1">
                  {user.last_login && (
                    <p>Last login: {new Date(user.last_login).toLocaleDateString()}</p>
                  )}
                  {user.pin_reset_required && (
                    <Badge variant="destructive" className="text-xs">PIN Reset Required</Badge>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openEditModal(user)}
                    disabled={user.id === currentUser?.id}
                    data-testid={`edit-user-${user.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openResetPinModal(user)}
                    data-testid={`reset-pin-${user.id}`}
                  >
                    <Key className="h-4 w-4" />
                  </Button>
                  {user.is_active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteUser(user.id)}
                      disabled={user.id === currentUser?.id}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleReactivateUser(user.id)}
                      data-testid={`reactivate-user-${user.id}`}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {users.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No users found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchQuery || roleFilter !== 'all' ? 'Try adjusting your filters' : 'Get started by adding your first user'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add User Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-modal">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new system user</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="user-first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="user-last-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <PhoneInput
                id="phone"
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value || '' })}
                placeholder="Enter phone number"
                required
                data-testid="user-phone-input"
              />
              <p className="text-xs text-gray-500">Default PIN will be the last 4 digits</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="office_admin">Office Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.role !== 'super_admin' && (
              <div className="space-y-2">
                <Label htmlFor="office">Assign to Office *</Label>
                <Select
                  value={formData.office_id}
                  onValueChange={(value) => setFormData({ ...formData, office_id: value })}
                >
                  <SelectTrigger data-testid="user-office-select">
                    <SelectValue placeholder="Select office" />
                  </SelectTrigger>
                  <SelectContent>
                    {offices.map(office => (
                      <SelectItem key={office.id} value={office.id}>{office.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="pin">Custom PIN (optional)</Label>
              <Input
                id="pin"
                type="password"
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                placeholder="Leave blank for default"
                maxLength={4}
                data-testid="user-pin-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-user-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-first_name">First Name *</Label>
                <Input
                  id="edit-first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="edit-user-first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last_name">Last Name *</Label>
                <Input
                  id="edit-last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="edit-user-last-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input value={formData.phone} disabled className="bg-gray-100" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="office_admin">Office Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.role !== 'super_admin' && (
              <div className="space-y-2">
                <Label htmlFor="edit-office">Assign to Office *</Label>
                <Select
                  value={formData.office_id}
                  onValueChange={(value) => setFormData({ ...formData, office_id: value })}
                >
                  <SelectTrigger data-testid="edit-user-office-select">
                    <SelectValue placeholder="Select office" />
                  </SelectTrigger>
                  <SelectContent>
                    {offices.map(office => (
                      <SelectItem key={office.id} value={office.id}>{office.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="update-user-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update User'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset PIN Modal */}
      <Dialog open={showResetPinModal} onOpenChange={setShowResetPinModal}>
        <DialogContent className="sm:max-w-md" data-testid="reset-pin-modal">
          <DialogHeader>
            <DialogTitle>Reset PIN</DialogTitle>
            <DialogDescription>
              Reset PIN for {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {pinResetResult ? (
              <Alert>
                <AlertDescription>
                  <p className="font-medium">{pinResetResult.message}</p>
                  {pinResetResult.new_pin && pinResetResult.new_pin !== 'Custom PIN set' && (
                    <p className="mt-2">New PIN: <span className="font-mono font-bold">{pinResetResult.new_pin}</span></p>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="new-pin">New PIN (optional)</Label>
                  <Input
                    id="new-pin"
                    type="password"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value)}
                    placeholder="Leave blank for default (last 4 digits of phone)"
                    maxLength={4}
                    data-testid="new-pin-input"
                  />
                </div>
                <Button
                  onClick={handleResetPin}
                  disabled={submitting}
                  className="w-full"
                  data-testid="confirm-reset-pin-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset PIN'
                  )}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageUsers;
